import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { inr, fmtDate } from "@/lib/format";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, TrendUp } from "@phosphor-icons/react";

const SOURCES = ["Consultation", "Other"];
const empty = () => ({ source: "Consultation", amount: 0, date: new Date().toISOString().slice(0, 10), notes: "" });

export default function AddIncome() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);
  const [customSource, setCustomSource] = useState(false);

  const load = useCallback(async () => { try { setRows((await api.get("/income")).data); } catch { toast.error("Failed to load income"); } }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(empty()); setCustomSource(false); setOpen(true); };

  const submit = async () => {
    const amount = Number(form.amount || 0);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    if (!form.source?.trim()) { toast.error("Enter an income source"); return; }
    setSaving(true);
    try { await api.post("/income", { ...form, amount }); toast.success("Income added"); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  const doDelete = async () => { try { await api.delete(`/income/${delId}`); toast.success("Removed"); load(); } catch { toast.error("Failed"); } finally { setDelId(null); } };

  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div data-testid="income-page">
      <PageHeader overline="Other Income" title="Add Income" subtitle="Record non-commission income (consultation, other). Feeds directly into the Combined Company P&L."
        actions={<Button data-testid="income-add" onClick={openCreate} className="rounded-sm bg-brand hover:bg-brand-dark"><Plus size={16} weight="bold" className="mr-1.5" /> Add Income</Button>} />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <div className="border border-zinc-200 bg-white rounded-sm p-4" data-testid="income-total-card">
          <div className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1.5"><TrendUp size={13} /> Total Other Income</div>
          <div className="text-xl font-heading tracking-tight mt-1 tabular-nums text-emerald-700">{inr(total)}</div>
          <div className="text-xs text-zinc-400 mt-0.5">{rows.length} entries</div>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white rounded-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-zinc-50 hover:bg-zinc-50">
            {["Date", "Source", "Amount", "Notes", "Added By", "Actions"].map((h) => (
              <TableHead key={h} className={`text-[11px] uppercase tracking-wider text-zinc-500 font-medium ${h === "Actions" ? "text-right" : ""}`}>{h}</TableHead>
            ))}
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-12 text-zinc-400">No income entries yet.</TableCell></TableRow>
              : rows.map((r) => (
                <TableRow key={r.id} data-testid="income-row" className="border-zinc-100 hover:bg-zinc-50">
                  <TableCell className="text-sm text-zinc-600 whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                  <TableCell className="text-sm font-medium text-zinc-900">{r.source || "—"}</TableCell>
                  <TableCell className="text-sm tabular-nums text-emerald-700">{inr(r.amount)}</TableCell>
                  <TableCell className="text-sm text-zinc-500 max-w-xs truncate">{r.notes || "—"}</TableCell>
                  <TableCell className="text-sm text-zinc-500">{r.created_by || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button data-testid="income-delete" variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-zinc-500 hover:text-red-600" onClick={() => setDelId(r.id)}><Trash size={16} /></Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">Add Income</DialogTitle><DialogDescription className="text-sm text-zinc-500">Record other income that feeds into the Combined Company P&L.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <Label className="text-xs text-zinc-500 mb-1.5 block">Source</Label>
              <Select value={customSource ? "Other" : form.source} onValueChange={(v) => { if (v === "Other") { setCustomSource(true); setForm((f) => ({ ...f, source: "" })); } else { setCustomSource(false); setForm((f) => ({ ...f, source: v })); } }}>
                <SelectTrigger data-testid="income-source" className="rounded-sm h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-sm">{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              {customSource && <Input data-testid="income-source-custom" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="rounded-sm h-10 mt-2" placeholder="Enter income source" />}
            </div>
            <div><Label className="text-xs text-zinc-500 mb-1.5 block">Date</Label><Input data-testid="income-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm h-10" /></div>
            <div className="col-span-2"><Label className="text-xs text-zinc-500 mb-1.5 block">Amount (₹)</Label><Input data-testid="income-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm h-10" /></div>
            <div className="col-span-2"><Label className="text-xs text-zinc-500 mb-1.5 block">Notes</Label><Textarea data-testid="income-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" /></div>
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="income-submit" onClick={submit} disabled={saving} className="rounded-sm bg-brand hover:bg-brand-dark">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader><AlertDialogTitle>Remove this income entry?</AlertDialogTitle><AlertDialogDescription>This permanently removes the record and its P&L contribution.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-income-delete" onClick={doDelete} className="rounded-sm bg-red-600 hover:bg-red-700">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
